"use client"

import * as React from "react"
import { createPortal } from "react-dom"
import Link from "next/link"
import { ArrowRight, CalendarClock, FileCheck2, MapPin, Truck, X } from "lucide-react"

import {
  BOOKING_POLICY_PARAGRAPHS,
  RETAINER_POLICY,
  TRAVEL_POLICY_FALLBACK,
  type PolicyTravelSection,
} from "@/lib/policy-content"
import { cn } from "@/lib/utils"

type PolicyType = 'booking' | 'travel'

type BookingDetailsSectionProps = {
  variant?: 'standard' | 'team-season'
  travelPolicy?: PolicyTravelSection
  className?: string
}

export function BookingDetailsSection({
  variant = 'standard',
  travelPolicy = TRAVEL_POLICY_FALLBACK,
  className,
}: BookingDetailsSectionProps) {
  const [openPolicy, setOpenPolicy] = React.useState<PolicyType | null>(null)
  const triggerRef = React.useRef<HTMLButtonElement | null>(null)
  const normalizedTravel = normalizeTravelPolicy(travelPolicy)
  const isTeamSeason = variant === 'team-season'
  const items = isTeamSeason ? teamSeasonDetails : standardDetails

  function openDialog(policy: PolicyType, trigger: HTMLButtonElement) {
    triggerRef.current = trigger
    setOpenPolicy(policy)
  }

  function closeDialog() {
    setOpenPolicy(null)
    window.requestAnimationFrame(() => {
      triggerRef.current?.focus()
    })
  }

  return (
    <section className={cn("rounded-3xl border bg-card p-6 shadow-sm md:p-8", className)}>
      <div className="grid gap-5 lg:grid-cols-[240px_minmax(0,1fr)] lg:items-start">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">Before you book</p>
          <h2 className="mt-2 text-2xl font-bold tracking-tight md:text-3xl">BOOKING DETAILS</h2>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">
            Quick terms up front, with the full details available when you need them.
          </p>
        </div>

        <div className={cn("grid gap-3", isTeamSeason ? "md:grid-cols-2" : "md:grid-cols-3")}>
          {items.map((item) => (
            <article key={item.title} className="flex min-h-full flex-col rounded-2xl border bg-background/70 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <item.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-sm font-semibold uppercase tracking-[0.14em] text-foreground">{item.title}</h3>
              <p className="mt-2 flex-1 text-sm leading-6 text-muted-foreground">{item.summary}</p>
              {item.action ? (
                <button
                  type="button"
                  onClick={(event) => openDialog(item.action.policy, event.currentTarget)}
                  className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-lg text-left text-sm font-semibold text-primary underline-offset-4 transition-colors hover:text-primary/80 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  {item.action.label}
                  <ArrowRight className="h-4 w-4" />
                </button>
              ) : null}
            </article>
          ))}
        </div>
      </div>

      <PolicyDialog
        openPolicy={openPolicy}
        travelPolicy={normalizedTravel}
        onClose={closeDialog}
      />
    </section>
  )
}

function PolicyDialog({
  openPolicy,
  travelPolicy,
  onClose,
}: {
  openPolicy: PolicyType | null
  travelPolicy: PolicyTravelSection
  onClose: () => void
}) {
  const [mounted, setMounted] = React.useState(false)
  const dialogRef = React.useRef<HTMLDivElement | null>(null)
  const titleId = React.useId()
  const descriptionId = React.useId()

  React.useEffect(() => {
    setMounted(true)
  }, [])

  React.useEffect(() => {
    if (!openPolicy) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',')

    window.requestAnimationFrame(() => {
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(focusableSelector)
      focusable?.[0]?.focus()
    })

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }

      if (event.key !== 'Tab' || !dialogRef.current) return

      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(focusableSelector))
      if (focusable.length === 0) {
        event.preventDefault()
        dialogRef.current.focus()
        return
      }

      const firstElement = focusable[0]
      const lastElement = focusable[focusable.length - 1]

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose, openPolicy])

  if (!mounted || !openPolicy) return null

  const title = openPolicy === 'booking' ? 'BOOKING + CANCELLATIONS' : 'TRAVEL'

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        className="relative flex max-h-[calc(100dvh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-3xl border bg-card text-card-foreground shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b p-5 md:p-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">Policy details</p>
            <h2 id={titleId} className="mt-2 text-2xl font-bold tracking-tight md:text-3xl">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-5 w-5" />
            <span className="sr-only">Close policy details</span>
          </button>
        </div>

        <div id={descriptionId} className="overflow-y-auto p-5 md:p-6">
          {openPolicy === 'booking' ? (
            <BookingPolicyContent />
          ) : (
            <TravelPolicyContent travelPolicy={travelPolicy} />
          )}
        </div>

        <div className="border-t bg-muted/25 p-5 md:p-6">
          <Link
            href="/policies"
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border bg-background px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={onClose}
          >
            View all policies
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>,
    document.body
  )
}

function BookingPolicyContent() {
  return (
    <div className="space-y-6">
      <div className="space-y-3 text-sm leading-6 text-muted-foreground">
        {BOOKING_POLICY_PARAGRAPHS.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>

      <section className="rounded-2xl border bg-background/70 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-foreground">Typical retainer</h3>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          Retainers are credited toward the total package price.
        </p>
        <div className="mt-4 grid gap-3">
          {RETAINER_POLICY.map((item) => (
            <div key={item.label} className="rounded-xl border bg-muted/30 p-3">
              <p className="text-sm font-semibold text-foreground">{item.label}</p>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">{item.value}</p>
              {item.reference ? (
                <p className="mt-1 text-sm leading-6 text-muted-foreground">{item.reference}</p>
              ) : null}
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}

function TravelPolicyContent({ travelPolicy }: { travelPolicy: PolicyTravelSection }) {
  return (
    <div className="space-y-6">
      <p className="text-sm leading-6 text-muted-foreground">{travelPolicy.description}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {travelPolicy.options.map((option) => (
          <div key={option.label} className="rounded-2xl border bg-background/70 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">{option.label}</p>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{option.description}</p>
            <p className="mt-3 font-sans text-xl font-bold tracking-tight">{option.price}</p>
          </div>
        ))}
      </div>
      <p className="rounded-2xl border bg-background/70 p-4 text-sm leading-6 text-muted-foreground">
        {travelPolicy.note}
      </p>
    </div>
  )
}

function normalizeTravelPolicy(travelPolicy: PolicyTravelSection): PolicyTravelSection {
  return {
    description: travelPolicy.description || TRAVEL_POLICY_FALLBACK.description,
    options: travelPolicy.options?.length ? travelPolicy.options : TRAVEL_POLICY_FALLBACK.options,
    note: travelPolicy.note || TRAVEL_POLICY_FALLBACK.note,
  }
}

const standardDetails = [
  {
    icon: FileCheck2,
    title: 'BOOKING RETAINER',
    summary: 'A signed photography agreement and booking retainer are required to reserve coverage.',
    action: {
      label: 'View booking & cancellation policy',
      policy: 'booking' as const,
    },
  },
  {
    icon: MapPin,
    title: 'TRAVEL',
    summary: 'Local travel from the Tri-Valley / Pleasanton area is included. Events farther out may include an additional travel fee.',
    action: {
      label: 'View travel policy',
      policy: 'travel' as const,
    },
  },
  {
    icon: CalendarClock,
    title: 'DELIVERY',
    summary: 'Standard galleries are typically delivered within 48-72 hours of the covered competition.',
  },
]

const teamSeasonDetails = [
  {
    icon: FileCheck2,
    title: 'BOOKING RETAINER',
    summary: 'A signed season agreement and 20% booking retainer are required before competition dates are reserved.',
    action: {
      label: 'View booking & cancellation policy',
      policy: 'booking' as const,
    },
  },
  {
    icon: Truck,
    title: 'TRAVEL + AWAY COMPETITIONS',
    summary: 'Season pricing assumes competition dates within the Tri-Valley / Pleasanton local service area. Away dates farther out may include travel fees.',
    action: {
      label: 'View travel policy',
      policy: 'travel' as const,
    },
  },
]
